'use client';

import React, { useState, useEffect } from 'react';
import { fetchGoodsReceiptBankAlignment } from '../../apiHelper';
import { Clock, ArrowRight, Zap, AlertCircle, Banknote, Calendar } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    return `₦${val.toLocaleString()}`;
};

const GoodsReceiptAlignmentDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadAlignmentData = async () => {
            try {
                const response = await fetchGoodsReceiptBankAlignment({
                    bank_account_id: 92,
                    lookback_days: 30
                });
                setData(response);
            } catch (err) {
                console.error("Error fetching alignment data:", err);
            } finally {
                setLoading(false);
            }
        };
        loadAlignmentData();
    }, []);

    if (loading) return <div className="animate-pulse h-80 bg-gray-50 rounded-3xl" />;

    const alignment = data?.current_receipt_alignment || {};
    const optimal = data?.optimal_receipt_windows?.[0] || {};
    const pattern = data?.bank_balance_patterns?.[0] || {};

    return (
        <div className="flex flex-col h-full">
            {/* Header & Savings Badge */}
            <div className="flex justify-between items-start mb-8">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">Receipt Alignment</h3>
                    <p className="text-xs text-gray-500 mt-1">Cash flow timing optimization</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 px-4 py-2 rounded-2xl text-right">
                    <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Potential Savings</p>
                    <p className="text-lg font-black text-emerald-700 leading-none">
                        {formatCurrency(data?.total_potential_savings_30d)}
                    </p>
                </div>
            </div>

            {/* Strategy Comparison Card */}
            <div className="bg-gray-50 rounded-3xl p-6 mb-6 relative overflow-hidden">
                <div className="relative z-10 flex justify-between items-center">
                    <div className="space-y-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Current State</p>
                        <p className="text-lg font-bold text-gray-700">{alignment.current_avg_receipt_day}</p>
                        <p className="text-xs text-gray-400">{alignment.current_avg_receipt_hour}</p>
                    </div>

                    <div className="flex flex-col items-center px-4">
                        <div className="h-px w-12 bg-gray-300 relative">
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-white px-2 text-[10px] font-bold text-red-500">
                                {alignment.misalignment_days}d Gap
                            </div>
                        </div>
                        <ArrowRight className="text-gray-300 mt-1" size={20} />
                    </div>

                    <div className="space-y-1 text-right">
                        <p className="text-[10px] font-bold text-[#4285F4] uppercase">Optimal Window</p>
                        <p className="text-lg font-bold text-[#4285F4]">{optimal.day_of_week}</p>
                        <p className="text-xs text-blue-400">{optimal.recommended_hour_window}</p>
                    </div>
                </div>

                {/* Background Decoration */}
                <Zap className="absolute -right-4 -bottom-4 text-blue-100 opacity-50" size={100} />
            </div>

            {/* Analysis Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="border border-gray-100 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Banknote size={16} className="text-gray-400" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Peak Balance</span>
                    </div>
                    <p className="text-sm font-bold text-gray-800">{formatCurrency(pattern.avg_peak_balance)}</p>
                    <p className="text-[10px] text-gray-400 mt-1">Average {pattern.peak_day_of_week}s</p>
                </div>
                <div className="border border-gray-100 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Clock size={16} className="text-gray-400" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Execution</span>
                    </div>
                    <p className="text-sm font-bold text-gray-800">{optimal.recommended_hour_window}</p>
                    <p className="text-[10px] text-gray-400 mt-1">Recommended Time</p>
                </div>
            </div>

            {/* Executive Summary Action */}
            <div className="mt-auto bg-blue-600 rounded-2xl p-5 text-white">
                <div className="flex gap-3">
                    <AlertCircle className="shrink-0 text-blue-200" size={20} />
                    <div>
                        <p className="text-xs font-medium leading-relaxed">
                            {data?.executive_summary.replace(/\*\*/g, '')}
                        </p>
                        <button className="mt-3 w-full bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-xl text-[10px] font-bold tracking-widest uppercase">
                            Reschedule Future Receipts
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GoodsReceiptAlignmentDashboard;